import React, { useState, useEffect, useMemo } from 'react';
import { Table } from 'antd';
import { useTranslation } from 'react-i18next';
import EditableDrawer from 'components/EditableDrawer';
import { useWarehouseContext } from '../../../../contexts/WarehouseContext';
import { TmsBusinessTypeEnum, TmsRouteTypeEnum, WaypointTypeEnum, HandoverTypeEnum } from '../../../../enums/TmsEnum';
import { getTmsRouteBusinessTypeLabel } from '../types/TmsScheduleType';
import type { TableColumnsType } from 'antd';

interface StationRow {
    key: string;
    type: 'origin' | 'destination' | 'waypoint';
    station: string;
    travelTime: number;
    dwellTime: number;
    distance: number;
}

interface WarehouseOption {
    id: string;
    sortingCode: string;
}

interface RouteDetailDrawerProps {
    visible: boolean;
    onClose: () => void;
    initialValues?: any;
    statusOptions: { label: string; value: number }[];
    routeTypeOptions: { label: string; value: number }[];
    warehouseOptions: WarehouseOption[];
    loading?: boolean;
    isHandoverTab?: boolean;
}

const RouteDetailDrawer: React.FC<RouteDetailDrawerProps> = ({
    visible,
    onClose,
    initialValues,
    statusOptions,
    routeTypeOptions,
    warehouseOptions,
    loading: hubLoading = false,
    isHandoverTab = false,
}) => {
    const { t } = useTranslation();
    const [filters, setFilters] = useState({
        routeType: undefined as number | undefined,
        carrierId: undefined as string | undefined,
        status: undefined as number | undefined
    });
    const { currentWarehouse } = useWarehouseContext();
    const [tableData, setTableData] = useState<StationRow[]>([
        { key: 'origin', type: 'origin', station: '', travelTime: 0, dwellTime: 0, distance: 0 },
        { key: 'destination', type: 'destination', station: '', travelTime: 0, dwellTime: 0, distance: 0 }
    ]);

    const isHandoverRoute = isHandoverTab || filters.routeType === TmsRouteTypeEnum.Handover || initialValues?.routeType === TmsRouteTypeEnum.Handover;
    // 交接方类型是否为干线承运商（经外部站点、有终点/途经点配置）；本地尾程商（含 TikTok、TemuGA DHLe）则无
    const isLinehaulCarrier = initialValues?.carrierSourceType === HandoverTypeEnum.LinehaulCarrier;
    // 是否展示途经点区域：交接路线仅干线承运商展示，本地尾程商无终点不展示；干线/支线直接展示
    const showWaypointSection = !isHandoverRoute || isLinehaulCarrier;

    // Initialize form data when viewing
    useEffect(() => {
        // Wait for warehouseOptions to be loaded
        if (initialValues && visible && warehouseOptions.length > 0) {
            console.log('Initializing view mode with data:', {
                initialValues,
                waypoints: initialValues.waypoints,
                warehouseOptions: warehouseOptions.length,
                waypointsLength: initialValues.waypoints?.length || 0
            });

            // Set filters
            setFilters({
                routeType: typeof initialValues.routeType === 'string' ? parseInt(initialValues.routeType) : initialValues.routeType,
                carrierId: typeof initialValues.carrierId === 'string' ? initialValues.carrierId : initialValues.carrierId?.toString(),
                status: typeof initialValues.status === 'string' ? parseInt(initialValues.status) : initialValues.status
            });

            // Build table data from waypoints
            if (initialValues.waypoints && Array.isArray(initialValues.waypoints) && initialValues.waypoints.length > 0) {
                
                // Sort waypoints by waypointOrder
                const sortedWaypoints = [...initialValues.waypoints].sort((a, b) => a.waypointOrder - b.waypointOrder);
                
                const newTableData: StationRow[] = [];
                
                // Find warehouse sorting code by id
                const getSortingCodeById = (warehouseId: number | string): string => {
                    const id = typeof warehouseId === 'string' ? parseInt(warehouseId) : warehouseId;
                    const warehouse = warehouseOptions.find(w => parseInt(w.id) === id);
                    const sortingCode = warehouse?.sortingCode || '';
                    return sortingCode;
                };

                const isExternalStationRoute =
                    initialValues.businessType === TmsBusinessTypeEnum.TemuGA || initialValues.businessType === TmsBusinessTypeEnum.TemuSCF;

                const resolveStationDisplay = (
                    waypoint: (typeof sortedWaypoints)[0],
                    sortingCode: string
                ): string => {
                    if (waypoint.waypointType === WaypointTypeEnum.Origin) {
                        return sortingCode;
                    }
                    if (
                        isExternalStationRoute &&
                        (waypoint.waypointType === WaypointTypeEnum.Waypoint ||
                            waypoint.waypointType === WaypointTypeEnum.Destination)
                    ) {
                        return (waypoint.waypointSortingCode || '').trim() || sortingCode || '-';
                    }
                    return sortingCode;
                };

                // Process waypoints in order
                for (let i = 0; i < sortedWaypoints.length; i++) {
                    const waypoint = sortedWaypoints[i];
                    const sortingCode = getSortingCodeById(waypoint.waypointId);
                    const stationDisplay = resolveStationDisplay(waypoint, sortingCode);
                    
                    // Get travelTime, dwellTime, distance from waypoint data
                    const travelTime = waypoint.travelTime ?? 0;
                    const dwellTime = waypoint.dwellTime ?? 0;
                    const distance = waypoint.distance ?? 0;
                    
                    if (waypoint.waypointType === WaypointTypeEnum.Origin) {
                        // Origin - no travel time/distance from previous, but has dwellTime
                        newTableData.push({
                            key: 'origin',
                            type: 'origin',
                            station: stationDisplay,
                            travelTime: 0,
                            dwellTime: dwellTime || 0,
                            distance: 0
                        });
                    } else if (waypoint.waypointType === WaypointTypeEnum.Waypoint) {
                        // Waypoint - has travel time/distance from previous
                        newTableData.push({
                            key: `waypoint-${waypoint.id}`,
                            type: 'waypoint',
                            station: stationDisplay,
                            travelTime: travelTime || 0,
                            dwellTime: dwellTime || 0,
                            distance: distance || 0
                        });
                    } else if (waypoint.waypointType === WaypointTypeEnum.Destination) {
                        // Destination - has travel time/distance from previous
                        newTableData.push({
                            key: 'destination',
                            type: 'destination',
                            station: stationDisplay,
                            travelTime: travelTime || 0,
                            dwellTime: dwellTime || 0,
                            distance: distance || 0
                        });
                    }
                }

                // Ensure origin and destination exist
                if (!newTableData.some(row => row.type === 'origin')) {
                    newTableData.unshift({
                        key: 'origin',
                        type: 'origin',
                        station: currentWarehouse?.sortingCode || '',
                        travelTime: 0,
                        dwellTime: 0,
                        distance: 0
                    });
                }
                if (!newTableData.some(row => row.type === 'destination')) {
                    newTableData.push({
                        key: 'destination',
                        type: 'destination',
                        station: '',
                        travelTime: 0,
                        dwellTime: 0,
                        distance: 0
                    });
                }

                setTableData(newTableData);
            } else {
                // No waypoints, use default structure
                setTableData([
                    { key: 'origin', type: 'origin', station: currentWarehouse?.sortingCode || '', travelTime: 0, dwellTime: 0, distance: 0 },
                    { key: 'destination', type: 'destination', station: '', travelTime: 0, dwellTime: 0, distance: 0 }
                ]);
            }
        } else if (initialValues && visible && warehouseOptions.length === 0) {
            // Warehouse options not loaded yet, wait for them
            console.log('Waiting for warehouseOptions to load...');
        }
    }, [initialValues, visible, warehouseOptions, currentWarehouse]);

    // 交接方/承运商名称由后端 getTmsRouteWithCarrierInfoById 直接联表返回（不过滤 status，禁用项照常显示），只读详情无需前端反查列表
    const carrierName = initialValues?.carrierName || '-';
    const routeTypeName = routeTypeOptions.find(r => r.value === filters.routeType)?.label || '-';
    const statusName = statusOptions.find(s => s.value === filters.status)?.label || '-';

    const routeBusinessTypeDisplay = useMemo(
        () => getTmsRouteBusinessTypeLabel(t, initialValues?.businessType ?? null),
        [t, initialValues?.businessType]
    );

    // Calculate total time and distance
    // totalHours: 包含所有行的travelTime + 所有行的dwellTime（包括起点和终点）
    const totalHours = tableData.reduce((sum, row) => {
        const travelTime = row.travelTime || 0;
        const dwellTime = row.dwellTime || 0;
        return sum + travelTime + dwellTime;
    }, 0).toFixed(1);
    const totalMiles = tableData.reduce((sum, row) => sum + row.distance, 0).toFixed(1);

    // Table columns - all read-only
    const columns: TableColumnsType<StationRow> = [
        {
            title: '',
            key: 'empty',
            width: 100,
            align: 'left',
            render: (_, record) => {
                if (record.type === 'origin') return t('tms.schedule.drawer.origin');
                if (record.type === 'destination') return t('tms.schedule.drawer.destination');
                if (record.type === 'waypoint') {
                    // Count waypoints before this one
                    const waypointIndex = tableData.filter(row => row.type === 'waypoint').findIndex(row => row.key === record.key);
                    return t('tms.schedule.drawer.waypointX', { index: waypointIndex + 1 });
                }
                return '';
            },
        },
        {
            title: t('tms.schedule.drawer.station'),
            dataIndex: 'station',
            key: 'station',
            align: 'left',
            render: (value: string) => value || '-',
        },
        {
            title: t('tms.schedule.drawer.travelTime'),
            dataIndex: 'travelTime',
            key: 'travelTime',
            align: 'left',
            render: (value: number, record: StationRow) => {
                if (record.type === 'origin') return '-';
                return value || 0;
            },
        },
        {
            title: t('tms.schedule.drawer.dwellTime'),
            dataIndex: 'dwellTime',
            key: 'dwellTime',
            align: 'left',
            render: (value: number) => {
                return value || 0;
            },
        },
        {
            title: t('tms.schedule.drawer.distance'),
            dataIndex: 'distance',
            key: 'distance',
            align: 'left',
            render: (value: number, record: StationRow) => {
                if (record.type === 'origin') return '-';
                return value || 0;
            },
        },
    ];

    const drawerTitle = initialValues?.routeName || 'Route Details';

    const handleClose = () => {
        onClose();
    };

    return (
        <EditableDrawer
            title={drawerTitle}
            visible={visible}
            onClose={handleClose}
            onSubmit={() => {}}
            confirmDisabled={true}
            width={1000}
            showDefaultFooter={false}
        >
            {/* Filter Section - Read-only */}
            <div className="bg-white mb-4">
                <div className="grid grid-cols-3 gap-4">
                    <div className="flex items-center">
                        <span className="text-gray-500 mr-2 text-left w-[6em]">{t('tms.schedule.drawer.type')}: </span>
                        <span className="flex-1">{routeTypeName}</span>
                    </div>

                    <div className="flex items-center">
                        <span className="text-gray-500 mr-2 text-left w-[6em]">{t(isHandoverRoute ? 'tms.schedule.drawer.handoverCarrier' : 'tms.schedule.drawer.carrier')}: </span>
                        <span className="flex-1">{carrierName}</span>
                    </div>

                    <div className="flex items-center">
                        <span className="text-gray-500 mr-2 text-left w-[6em]">{t('tms.schedule.drawer.status')}: </span>
                        <span className="flex-1">{statusName}</span>
                    </div>
                </div>
                <div className="grid grid-cols-3 gap-4 mt-2">
                    <div className="flex items-center">
                        <span className="text-gray-500 mr-2 text-left w-[6em]">{t('tms.schedule.drawer.routeBusinessType')}: </span>
                        <span className="flex-1">{routeBusinessTypeDisplay || '-'}</span>
                    </div>
                </div>
            </div>

            {showWaypointSection && (
                <>
                    {/* Summary Section */}
                    <div className="bg-white mb-4 p-3 border border-gray-200">
                        <div className="flex gap-3">
                            <span>{t('tms.schedule.drawer.estimatedTotalHours')}: </span><span className="font-bold">{totalHours} {t('tms.schedule.drawer.hours')}</span>
                            <span>{t('tms.schedule.drawer.estimatedTotalDistances')}: </span><span className="font-bold">{totalMiles} {t('tms.schedule.drawer.miles')}</span>
                        </div>
                    </div>

                    {/* Table Section */}
                    <div className="mb-4">
                        <Table
                            columns={columns}
                            dataSource={tableData}
                            pagination={false}
                            size="middle"
                            className="mb-4"/>
                    </div>
                </>
            )}
        </EditableDrawer>
    );
};

export default RouteDetailDrawer;

